import { useAuth, UserRole } from "@/features/auth";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RSocketTopic } from "../realtime-topics";
import { Event, TopicMessageHandler, TopicSubscription } from "../types";
import { normalizeError } from "../utils/errorHandling";
import { useRSocketContext } from "./useRSocket";

interface UseRSocketTopicOptions<T extends Event> {
    topicName: RSocketTopic | null;
    onMessage?: TopicMessageHandler<T>;
    enabled?: boolean;
    autoSubscribe?: boolean;
    allowedRoles: readonly UserRole[];
}

interface UseRSocketTopicResult<T extends Event> {
    isSubscribed: boolean;
    lastMessage: T | null;
    subscribe: () => Promise<void>;
    unsubscribe: () => void;
    error: Error | null;
}

/**
 * Hook for subscribing to RSocket topics
 * @param options Subscription options
 * @returns Subscription state and control functions
 */
export function useRSocketTopic<T extends Event>({
    topicName,
    onMessage,
    enabled = true,
    autoSubscribe = true,
    allowedRoles,
}: UseRSocketTopicOptions<T>): UseRSocketTopicResult<T> {
    const { isConnected, subscribeTopic } = useRSocketContext();
    const { hasRole } = useAuth();
    const hasAllowedRole = useMemo(() => allowedRoles.some((role) => hasRole(role)), [hasRole, allowedRoles]);
    const [subscription, setSubscription] = useState<TopicSubscription | null>(null);
    const [lastMessage, setLastMessage] = useState<T | null>(null);
    const [error, setError] = useState<Error | null>(null);
    const currentTopicRef = useRef<RSocketTopic | null>(null);
    const isSubscribed = subscription?.isSubscribed() ?? false;

    // Use refs to avoid stale closure issues and prevent race conditions
    const onMessageRef = useRef(onMessage);
    const subscriptionRef = useRef<TopicSubscription | null>(null);
    const isSubscribingRef = useRef(false);
    const isMountedRef = useRef(true);

    // Update the ref when onMessage changes
    useEffect(() => {
        onMessageRef.current = onMessage;
    }, [onMessage]);

    // Keep subscriptionRef in sync with state
    useEffect(() => {
        subscriptionRef.current = subscription;
    }, [subscription]);

    const handleMessage = useCallback((message: T) => {
        setLastMessage(message);
        if (onMessageRef.current) {
            onMessageRef.current(message);
        }
    }, []);

    // // Prevent effect re-runs on token refresh
    const subscribeTopicRef = useRef(subscribeTopic);
    useEffect(() => {
        subscribeTopicRef.current = subscribeTopic;
    }, [subscribeTopic]);

    const cancelCurrentSubscription = useCallback(() => {
        if (subscriptionRef.current) {
            console.debug(`Cancelling subscription to topic: ${currentTopicRef.current}`);
            subscriptionRef.current.cancel();
            subscriptionRef.current = null;
            setSubscription(null);
            currentTopicRef.current = null;
        }
    }, []);

    const performSubscription = useCallback(async () => {
        // Prevent concurrent subscription attempts. topicName === null means
        // the caller isn't ready yet (e.g., apiDomain not resolved) — bail out.
        if (isSubscribingRef.current || topicName === null) {
            if (isSubscribingRef.current) {
                console.debug(`Already subscribing to topic, skipping: ${topicName}`);
            }
            return null;
        }

        try {
            isSubscribingRef.current = true;
            setError(null);
            const newSubscription = await subscribeTopicRef.current<T>(topicName, handleMessage);
            // If the component unmounted while we were awaiting, drop the
            // subscription instead of stashing it, otherwise it leaks.
            if (!isMountedRef.current) {
                newSubscription.cancel();
                return null;
            }
            subscriptionRef.current = newSubscription;
            setSubscription(newSubscription);
            currentTopicRef.current = topicName;
            return newSubscription;
        } catch (err) {
            const error = normalizeError(err);
            console.error(`Error subscribing to topic ${topicName}:`, error);
            setError(error);
        } finally {
            isSubscribingRef.current = false;
        }
    }, [topicName, handleMessage]);

    const subscribe = useCallback(async () => {
        const currentlySubscribed = subscriptionRef.current?.isSubscribed() ?? false;

        if (!isConnected || !enabled || !hasAllowedRole || currentlySubscribed || topicName === null) {
            return;
        }

        await performSubscription();
    }, [isConnected, enabled, hasAllowedRole, topicName, performSubscription]);

    const unsubscribe = useCallback(() => {
        cancelCurrentSubscription();
    }, [cancelCurrentSubscription]);

    // Auto-subscribe when connected, enabled and authorized, or when topic name changes
    useEffect(() => {
        if (!autoSubscribe || !isConnected || !enabled || !hasAllowedRole || topicName === null) {
            return;
        }

        // Check if topic name has changed - if so, unsubscribe from old topic first
        const topicChanged = currentTopicRef.current !== null && currentTopicRef.current !== topicName;

        if (topicChanged) {
            console.debug(`Topic changed from ${currentTopicRef.current} to ${topicName}, unsubscribing from old topic`);
            cancelCurrentSubscription();
        }

        // Don't subscribe if already subscribed to the current topic
        if (subscriptionRef.current?.isSubscribed() && currentTopicRef.current === topicName) {
            return;
        }

        console.debug(`Auto-subscribing to topic: ${topicName}`);
        void performSubscription();

        // Cleanup: use ref to always have the latest subscription
        return () => {
            cancelCurrentSubscription();
        };
    }, [autoSubscribe, isConnected, enabled, hasAllowedRole, topicName, performSubscription, cancelCurrentSubscription]);

    // Fail-safe unmount cleanup. The auto-subscribe effect's cleanup already
    // cancels on unmount/topic-change/gate-flip, but this guarantees a cancel
    // even if that effect was never registered or if it early-returned before
    // we got a chance to hold a subscription.
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            cancelCurrentSubscription();
        };
    }, [cancelCurrentSubscription]);

    return {
        isSubscribed,
        lastMessage,
        subscribe,
        unsubscribe,
        error,
    };
}
